import React, { useRef, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { motion, AnimatePresence } from 'framer-motion'

type PageSliderProps<T> = {
  pages: T[]
  renderPage: (page: T, index: number, selected: boolean) => React.ReactNode
  selectedIndex: number
  onSelect: (index: number) => void
  className?: string
  style?: React.CSSProperties
  showArrows?: boolean
  pageWidth?: number | string // e.g. 340 or '22rem'
  responsive?: boolean // If true, pageWidth is ignored and flex-basis is used
  addPageButton?: React.ReactNode // NEW: slot for AddPageButton
}

export function PageSlider<T>({
  pages,
  renderPage,
  selectedIndex,
  onSelect,
  className = '',
  style,
  showArrows = true,
  pageWidth = 340,
  responsive = false,
  addPageButton,
}: PageSliderProps<T>) {
  const sliderRef = useRef<HTMLDivElement>(null)

  // Scroll to selected page when selectedIndex changes
  useEffect(() => {
    if (!sliderRef.current) return
    const slider = sliderRef.current
    const selected = slider.children[selectedIndex] as HTMLElement | undefined
    if (selected) {
      // Scroll so that the selected page is centered (if possible)
      const sliderRect = slider.getBoundingClientRect()
      const selectedRect = selected.getBoundingClientRect()
      const scrollLeft =
        selected.offsetLeft -
        sliderRect.width / 2 +
        selectedRect.width / 2
      slider.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      })
    }
  }, [selectedIndex, pages.length])

  // Keyboard navigation (left/right)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        if (selectedIndex > 0) {
          onSelect(selectedIndex - 1)
        }
      }
      if (e.key === 'ArrowRight') {
        if (selectedIndex < pages.length - 1) {
          onSelect(selectedIndex + 1)
        }
      }
    },
    [selectedIndex, onSelect, pages.length],
  )

  // Arrow button handlers
  const goPrev = () => {
    if (selectedIndex > 0) {
      onSelect(selectedIndex - 1)
    }
  }
  const goNext = () => {
    if (selectedIndex < pages.length - 1) {
      onSelect(selectedIndex + 1)
    }
  }

  // Fixed page width and improved overflow management
  const pageStyle: React.CSSProperties = {
    width: typeof pageWidth === 'number' ? `${pageWidth}px` : pageWidth,
    minWidth: typeof pageWidth === 'number' ? `${pageWidth}px` : pageWidth,
    maxWidth: typeof pageWidth === 'number' ? `${pageWidth}px` : pageWidth,
    flex: '0 0 auto',
    overflow: 'hidden',
  }

  return (
    <div
      className={clsx(
        'relative w-full',
        className,
      )}
      style={style}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="Pages du site"
    >
      {/* Arrows */}
      {showArrows && (
        <>
          <button
            type="button"
            className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 shadow p-2 border border-gray-200 transition hover:bg-brixel/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-brixel',
              selectedIndex === 0 && 'opacity-40 pointer-events-none',
              'hidden sm:flex',
            )}
            onClick={goPrev}
            aria-label="Page précédente"
            disabled={selectedIndex === 0}
            tabIndex={0}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            className={clsx(
              'absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 shadow p-2 border border-gray-200 transition hover:bg-brixel/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-brixel',
              selectedIndex === pages.length - 1 && 'opacity-40 pointer-events-none',
              'hidden sm:flex',
            )}
            onClick={goNext}
            aria-label="Page suivante"
            disabled={selectedIndex === pages.length - 1}
            tabIndex={0}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Add page button fixed on the right */}
      {addPageButton && (
        <div className="fixed right-6 bottom-8 z-30 sm:static sm:right-0 sm:bottom-0 sm:mb-0 sm:mr-0">
          {addPageButton}
        </div>
      )}

      {/* Slider */}
      <div
        ref={sliderRef}
        className={clsx(
          'flex gap-6 overflow-x-auto hide-scrollbar py-2 px-1 sm:px-8',
          'scroll-smooth',
        )}
        style={{
          scrollSnapType: 'x mandatory',
          overflowX: 'auto',
          overflowY: 'visible',
        }}
        tabIndex={-1}
      >
        <AnimatePresence initial={false}>
          {pages.map((page, i) => (
            <motion.div
              key={(page as any).id ?? i}
              layout
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.25 }}
              className={clsx(
                'cursor-pointer transition-shadow scroll-snap-align-center',
                i === selectedIndex
                  ? 'ring-2 ring-brixel shadow-lg z-10'
                  : 'ring-1 ring-gray-100 shadow-md hover:ring-brixel/40',
                'bg-white rounded-xl',
              )}
              style={{
                ...pageStyle,
                marginLeft: i === 0 ? 0 : undefined,
                marginRight: i === pages.length - 1 ? 0 : undefined,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
              tabIndex={0}
              aria-current={i === selectedIndex}
              aria-label={`Page ${i + 1}`}
              onClick={() => onSelect(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSelect(i)
                }
              }}
            >
              {renderPage(page, i, i === selectedIndex)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination dots (mobile) */}
      <div className="flex justify-center gap-2 mt-3 sm:hidden">
        {pages.map((_, i) => (
          <button
            key={i}
            type="button"
            className={clsx(
              'w-2.5 h-2.5 rounded-full transition',
              i === selectedIndex
                ? 'bg-brixel'
                : 'bg-gray-300 hover:bg-brixel/40',
            )}
            aria-label={`Aller à la page ${i + 1}`}
            onClick={() => onSelect(i)}
            tabIndex={0}
          />
        ))}
      </div>
    </div>
  )
}

export default PageSlider
